import { ref } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../../../consts";
import { adjustDate } from "../../../helpers";
import { useRegenerationPolling } from "./useRegenerationPolling";
import { useVideoGenerationPolling } from "./useVideoGenerationPolling";
import { useConfirmAction } from "./useConfirmAction";

export function useSnippetsLibrary(token) {
  const toast = useToast();
  const { confirmAction } = useConfirmAction();

  const snippets = ref([]);
  const totalSnippets = ref(0);
  const currentPage = ref(1);
  const searchQuery = ref("");
  let searchDebounceTimer = null;
  const loading = ref(false);
  const dateRange = ref(null);
  const snippetIdFilter = ref(null);

  const pendingRegenerationIds = ref(new Set());
  const justRegeneratedIds = ref(new Set());
  const regeneratingClickedId = ref(null);
  const generatingVideoClickedId = ref(null);

  const showTranscriptDialog = ref(false);
  const selectedTranscript = ref("");

  const { syncStatusStream } = useRegenerationPolling(token, () =>
    fetchSnippets()
  );
  const { syncStatusStream: syncVideoStatusStream } = useVideoGenerationPolling(
    token,
    () => fetchSnippets()
  );

  let latestRequestId = 0;

  const fetchSnippets = async () => {
    const requestId = ++latestRequestId;
    try {
      loading.value = true;
      const startDate = dateRange.value?.[0]
        ? adjustDate(dateRange.value[0]).toISOString().split("T")[0]
        : undefined;
      const endDate = dateRange.value?.[1]
        ? adjustDate(dateRange.value[1]).toISOString().split("T")[0]
        : undefined;

      const response = await axios.get(`${API_BASE_URL}/snippets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage.value,
          search: searchQuery.value || undefined,
          startDate,
          endDate,
          snippetId: snippetIdFilter.value || undefined,
        },
      });
      if (requestId !== latestRequestId) return;

      if (response.data?.success) {
        snippets.value = response.data.data.snippets;
        totalSnippets.value = response.data.data.total;

        for (const snippet of snippets.value) {
          if (
            pendingRegenerationIds.value.has(snippet.id) &&
            !snippet.regenerating
          ) {
            pendingRegenerationIds.value.delete(snippet.id);
            justRegeneratedIds.value.add(snippet.id);
          }
        }

        syncStatusStream(
          snippets.value.filter((s) => s.regenerating).map((s) => s.id)
        );
        syncVideoStatusStream(
          snippets.value
            .filter((s) => s.video_generating)
            .map((s) => s.snippet_id)
        );
      }
    } catch (error) {
      if (requestId !== latestRequestId) return;
      console.error("Error fetching snippets:", error);
    } finally {
      if (requestId === latestRequestId) loading.value = false;
    }
  };

  const setDateRange = (value) => {
    dateRange.value = value;
    snippetIdFilter.value = null;
    currentPage.value = 1;
    fetchSnippets();
  };

  const clearDateRange = () => {
    dateRange.value = null;
    currentPage.value = 1;
    fetchSnippets();
  };

  const onPage = (event) => {
    currentPage.value = event.page + 1;
    fetchSnippets();
  };

  const onSearchInput = () => {
    snippetIdFilter.value = null;
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      currentPage.value = 1;
      fetchSnippets();
    }, 400);
  };

  const refreshSnippets = () => {
    justRegeneratedIds.value.clear();
    fetchSnippets();
  };

  const resetFilters = () => {
    searchQuery.value = "";
    dateRange.value = null;
    snippetIdFilter.value = null;
    currentPage.value = 1;
  };

  const openTranscript = (snippet) => {
    selectedTranscript.value = snippet.metadata?.transcript_text || "";
    showTranscriptDialog.value = true;
  };

  const regenerateSnippet = async (snippet) => {
    regeneratingClickedId.value = snippet.id;
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/${snippet.id}/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: "Snippet regeneration has been queued.",
        life: 4000,
      });
      justRegeneratedIds.value.delete(snippet.id);
      pendingRegenerationIds.value.add(snippet.id);
      await fetchSnippets();
    } catch (error) {
      console.error("Error regenerating snippet:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to regenerate snippet.",
        life: 4000,
      });
    } finally {
      regeneratingClickedId.value = null;
    }
  };

  const confirmRegenerateSnippet = (snippet) => {
    confirmAction({
      message: "Are you sure you want to regenerate this snippet?",
      header: "Regenerate Confirmation",
      accept: () => regenerateSnippet(snippet),
    });
  };

  const generateVideo = async (snippet) => {
    generatingVideoClickedId.value = snippet.snippet_id;
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/by-snippet-id/${snippet.snippet_id}/generate-video`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: "Video generation has been queued.",
        life: 4000,
      });
      await fetchSnippets();
    } catch (error) {
      console.error("Error queuing video generation:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to queue video generation.",
        life: 4000,
      });
    } finally {
      generatingVideoClickedId.value = null;
    }
  };

  const confirmGenerateVideo = (snippet) => {
    confirmAction({
      message: "Are you sure you want to generate a video for this snippet?",
      header: "Generate Video Confirmation",
      accept: () => generateVideo(snippet),
    });
  };

  const setSnippetDisabled = async (snippet, disabled) => {
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/${snippet.id}/disable`,
        { disabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchSnippets();
    } catch (error) {
      console.error("Error updating snippet:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update snippet.",
        life: 4000,
      });
    }
  };

  const confirmDisableSnippet = (snippet) => {
    confirmAction({
      message:
        "Are you sure you want to disable this snippet? It will be excluded from playlists with restricted validation.",
      header: "Disable Confirmation",
      danger: true,
      accept: () => setSnippetDisabled(snippet, true),
    });
  };

  const confirmEnableSnippet = (snippet) => {
    confirmAction({
      message:
        "Are you sure you want to enable this snippet? It will be available for playlists with restricted validation.",
      header: "Enable Confirmation",
      accept: () => setSnippetDisabled(snippet, false),
    });
  };

  const toggleSnippetEnabled = (snippet, enabled) => {
    if (enabled) {
      confirmEnableSnippet(snippet);
    } else {
      confirmDisableSnippet(snippet);
    }
  };

  return {
    snippets,
    totalSnippets,
    currentPage,
    searchQuery,
    loading,
    dateRange,
    snippetIdFilter,
    setDateRange,
    clearDateRange,
    onPage,
    onSearchInput,
    resetFilters,
    fetchSnippets,
    refreshSnippets,
    showTranscriptDialog,
    selectedTranscript,
    openTranscript,
    regeneratingClickedId,
    justRegeneratedIds,
    confirmRegenerateSnippet,
    generatingVideoClickedId,
    confirmGenerateVideo,
    toggleSnippetEnabled,
  };
}
